
import { T } from "@/i18n/T";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Minus, BarChart3, RefreshCw } from "lucide-react";
import { SentimentAnalysis } from "@/hooks/useArticleAI";

interface SentimentWidgetProps {
  data: SentimentAnalysis | null;
  loading: boolean;
  onRefresh?: () => void;
  isRTL?: boolean;
}

const SentimentWidget = ({ data, loading, onRefresh, isRTL = false }: SentimentWidgetProps) => {
  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'BULLISH':
        return <TrendingUp className="h-4 w-4" />;
      case 'BEARISH':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const getSentimentStyles = (sentiment: string) => {
    switch (sentiment) {
      case 'BULLISH':
        return {
          badge: 'bg-success/20 text-success border-success/50',
          progress: 'bg-success',
          card: 'border-success/30'
        };
      case 'BEARISH':
        return {
          badge: 'bg-destructive/20 text-destructive border-destructive/50',
          progress: 'bg-destructive',
          card: 'border-destructive/30'
        };
      default:
        return {
          badge: 'bg-warning/20 text-rank-gold dark:text-rank-gold border-warning/50',
          progress: 'bg-warning',
          card: 'border-warning/30'
        };
    }
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 80) return "Very confident";
    if (confidence >= 60) return "Confident";
    if (confidence >= 40) return "Moderate";
    return "Low confidence";
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-byteline-green" />
            <T k="ai.marketSentiment" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-4 w-12" />
          </div>
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-byteline-green" />
            <T k="ai.marketSentiment" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            <T k="auto.analyzing-article-sentiment" />
          </p>
        </CardContent>
      </Card>
    );
  }

  const styles = getSentimentStyles(data.sentiment);

  return (
    <Card className={`border ${styles.card}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4 text-byteline-green" />
            <T k="ai.marketSentiment" />
          </CardTitle>
          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={loading}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-10 md:gap-3">
          {/* Left Section - Sentiment Badge (20%) */}
          <div className="md:col-span-2 flex flex-col items-center justify-center p-4 md:p-3 bg-secondary/50 rounded-lg">
            <div className={`p-2 rounded-full ${styles.badge} mb-2`}>
              {getSentimentIcon(data.sentiment)}
            </div>
            <Badge className={`${styles.badge} text-xs px-3 py-1 mb-1`}>
              {data.sentiment}
            </Badge>
            <div className="text-2xl font-bold">{data.confidence}%</div>
          </div>

          {/* Middle Section - What This Means (40%) */}
          <div className="md:col-span-4 space-y-2">
            <h4 className={`text-sm font-semibold text-muted-foreground border-b border-border pb-1 ${isRTL ? 'text-right' : ''}`}>
              <T k="ai.whatThisMeans" />
            </h4>
            <p className={`text-sm text-foreground leading-relaxed ${isRTL ? 'text-right' : ''}`}>
              {data.marketImplications}
            </p>
          </div>

          {/* Right Section - Key Factors (40%) */}
          <div className="md:col-span-4 space-y-2">
            <h4 className={`text-sm font-semibold text-muted-foreground border-b border-border pb-1 ${isRTL ? 'text-right' : ''}`}>
              <T k="ai.keyFactors" />
            </h4>
            <div className={`flex flex-wrap gap-1 ${isRTL ? 'justify-end' : ''}`}>
              {data.sentimentDrivers.map((driver, index) => (
                <Badge 
                  key={index} 
                  variant="secondary" 
                  className="text-xs px-2 py-0.5"
                >
                  {driver}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SentimentWidget;
